/* sb-dc-logoswap.js - swap a logo, a shape or a piece of text for the
   customer's own image, everywhere it repeats. 2026-09-14 (v12).

   v12 - WHY ONLY THE TILE YOU CLICKED CHANGED
   On step-and-repeat-vote (tpl 6ab57763) the bar offered "this shape" with no
   "and N more": 30 identical-looking marks and the matcher found one. Single
   shapes were matched on a string that demanded the path text be the same
   LENGTH to the character and the box agree once rounded to 0.02in. Converted
   template tiles carry their own baked coordinates, so neither holds. They are
   now compared to a snapshot of the picked shape with a tolerance on each
   measure - see shapeRef / shapeLike. Colour, stroke and path-length class
   still have to agree, so a red star does not collect blue capitols.

   WHAT IT OFFERS, depending on what is selected:
     a logo (several shapes that touch)  -> "Replace logo"
     one shape                           -> "Replace with image"
     a piece of text                     -> "Replace with image"
   The uploaded image is fitted into that object's box (contain, never
   stretched), the object is removed, and every identical copy on the board is
   replaced too. The bar says how many will change before the customer commits.

   v11 - WHY THE FIRST TILE GOT THE PICTURE TWICE
   Reported 2026-09-14 with a screenshot: after Replace, the top left tile of a
   step and repeat board carried two copies of the uploaded image stacked on
   each other. removeLayer is NOT synchronous - the layer list still carries a
   layer for a frame or two after it is removed - and v10 removed a tile's
   shapes and then looked for remaining matches IN THE SAME TURN. The tile it
   had just cleared was still in the list, still matched the signature, and so
   the next pass duplicated a second image into the same box. spread() then
   re-swept up to ROUNDS times, which could stack a third.
   Three things now stop that:
     1. removeAnd() waits for the layers to actually leave the list before
        anything looks at the board again.
     2. every box that has been filled this session is remembered (placed[]),
        and a match whose centre falls inside one of them is skipped.
     3. a box that already has an image of roughly its own size sitting over
        its centre is skipped as well. A full board background is much larger
        than a tile, so it is not mistaken for one.
   v11 also drops a pending swap when the Image panel is closed without a file.
   v10 stayed armed for WAIT_MS (3 minutes) after "Replace logo" was pressed,
   so an unrelated upload made in that window was taken as the swap's file and
   spread across the board.

   WHAT COUNTS AS IDENTICAL
     logo   same number of pieces, same set of colours, same overall aspect
            (to 0.1), and the same piece areas as a fraction of the logo
            (to 0.1). Coarse enough to survive rounding, specific enough that
            a different mark does not match.
     shape  same shape kind, colour and stroke exactly; path length, size and
            aspect within a tolerance (v12).
     text   same words, font, size, weight, slant, colour and alignment

   THE BAR is the editor's own image toolbar, reused: same markup and CSS
   values as #sbdc-imgtools-bar, same place above the canvas, its own id.

   PICKING: sb-dc-logopads.js lays invisible pads over multi-shape logos,
   because those are stroke-only paths and the canvas only answers on the ink.

   v3's PILE OF LOGOS: the code read the layer list from a captured `api`
   object, so a duplicate never appeared in that snapshot and it returned early
   - before moving the copy and before clearing the tile. Every step now
   re-reads window.__dcApi.front.layers.
   v6's FLICKER: sb-dc-tilesync.js treated our deletions as customer edits
   and propagated mid-swap. It is switched off for the duration.

   Diagnostics: window.__dcLogoSwap() */
(function () {
  'use strict';

  var TICK = 400, WAIT_MS = 180000, GAP_IN = 0.5, FIT = 0.98;
  var FIND_MS = 1500, POLL_MS = 40, MAX_TILES = 60, ROUNDS = 3, TOL = 0.02;
  var PANEL_GRACE = 5;          // ticks the Image panel may be missing for
  var SLOT_MAX = 1.5;           // an image this much bigger than the box is not "in" it
  var LEN_TOL = 0.15;           // path length may differ by this much
  var SIZE_TOL = 0.25;          // width and height may differ by this much
  var ASP_TOL = 0.15;           // and the aspect by this much
  var BAR_ID = 'sbdc-logotools-bar';

  var info = { groups: 0, offered: false, kind: '', matches: 0, swapped: 0,
               siblings: 0, skipped: 0, occupied: 0, rounds: 0, via: '', why: '' };
  window.__dcLogoSwap = function () { return info; };

  function live() {
    var a = window.__dcApi;
    return (a && a.front && Array.isArray(a.front.layers)
            && typeof a.updateLayer === 'function'
            && typeof a.removeLayer === 'function') ? a : null;
  }
  function layers() {
    var a = live();
    return a ? (a.doc || a.front).layers : [];
  }

  function box(l) { return { x: +l.xIn || 0, y: +l.yIn || 0, w: +l.widthIn || 0, h: +l.heightIn || 0 }; }
  function near(A, B) {
    return !(A.x > B.x + B.w + GAP_IN || A.x + A.w + GAP_IN < B.x ||
             A.y > B.y + B.h + GAP_IN || A.y + A.h + GAP_IN < B.y);
  }

  function groups() {
    var S = layers().filter(function (l) { return l.type === 'shape' && !l.locked; });
    var used = {}, out = [];
    S.forEach(function (s, i) {
      if (used[i]) return;
      var g = [i]; used[i] = 1;
      var grew = true;
      while (grew) {
        grew = false;
        S.forEach(function (t, j) {
          if (used[j]) return;
          var B = box(t);
          for (var k = 0; k < g.length; k++) {
            if (near(box(S[g[k]]), B)) { used[j] = 1; g.push(j); grew = true; return; }
          }
        });
      }
      out.push(g.map(function (k) { return S[k]; }));
    });
    info.groups = out.length;
    return out;
  }

  function bboxOf(g) {
    var x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9;
    g.forEach(function (l) {
      var b = box(l);
      x0 = Math.min(x0, b.x); y0 = Math.min(y0, b.y);
      x1 = Math.max(x1, b.x + b.w); y1 = Math.max(y1, b.y + b.h);
    });
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  }

  function r(v, step) { return (Math.round((+v || 0) / step) * step).toFixed(2); }

  function sigLogo(g) {
    var b = bboxOf(g);
    var fills = {}, list = [];
    g.forEach(function (l) { fills[l.fill || ''] = 1; });
    Object.keys(fills).sort().forEach(function (f) { list.push(f); });
    var areas = g.map(function (l) {
      return r(((+l.widthIn) * (+l.heightIn)) / ((b.w * b.h) || 1), 0.1);
    }).sort().join(',');
    return [g.length, list.join(','), r(b.w / (b.h || 1), 0.1), areas].join('|');
  }
  function sigShape(l) {
    return ['shape', l.shape || '', l.fill || '', l.pathStroke || '',
            String(l.pathData || '').length, r(l.widthIn, TOL), r(l.heightIn, TOL)].join('|');
  }

  /* v12: single shapes are NOT matched on that string any more.

     sigShape asked for the width and the height to agree once rounded to
     0.02in, and for the path text to be the same LENGTH to the character. On
     a converted template neither holds: the tiles carry their own baked
     coordinates, so the path text differs by a few characters from tile to
     tile, and the box lands either side of a rounding boundary. Measured on
     step-and-repeat-vote (6ab57763): 30 marks, every star identical to the
     eye, and the bar offered "this shape" - one - so nothing spread.

     What replaces it: the candidate is compared to a SNAPSHOT of the shape the
     customer picked, with a tolerance on each measure instead of equality.
     Colour and stroke still have to match exactly - that is what keeps a red
     star apart from a blue one - and the path length still has to be in the
     same class, which is what keeps a star apart from a capitol or a ballot
     box. Only the numbers that drift are given room. */
  function shapeRef(l) {
    return {
      shape: l.shape || '',
      fill: l.fill || '',
      stroke: l.pathStroke || '',
      len: String(l.pathData || '').length,
      w: +l.widthIn || 0,
      h: +l.heightIn || 0
    };
  }
  function within(a, b, tol) {
    var big = Math.max(Math.abs(a), Math.abs(b));
    if (!big) return true;
    return Math.abs(a - b) / big <= tol;
  }
  function shapeLike(l, ref) {
    if (!ref) return false;
    if ((l.shape || '') !== ref.shape) return false;
    if ((l.fill || '') !== ref.fill) return false;
    if ((l.pathStroke || '') !== ref.stroke) return false;
    if (!within(String(l.pathData || '').length, ref.len, LEN_TOL)) return false;
    var w = +l.widthIn || 0, h = +l.heightIn || 0;
    if (!within(w, ref.w, SIZE_TOL) || !within(h, ref.h, SIZE_TOL)) return false;
    return within(w / (h || 1), ref.w / (ref.h || 1), ASP_TOL);
  }
  /* the reference travels as a snapshot because the picked shape is deleted
     part way through the swap */
  function refOf(t) {
    return (t && t.kind === 'shape' && t.g && t.g[0]) ? shapeRef(t.g[0]) : null;
  }
  function sigText(l) {
    return ['text', String(l.text == null ? '' : l.text), l.fill || '', l.fontFamily || '',
            r(l.fontSizeIn, TOL), !!l.bold, !!l.italic, l.align || ''].join('|');
  }
  function sigOf(t) {
    if (t.kind === 'logo') return sigLogo(t.g);
    if (t.kind === 'text') return sigText(t.g[0]);
    return sigShape(t.g[0]);
  }

  function matches(kind, sig, ref) {
    if (kind === 'logo') {
      return groups().filter(function (g) { return g.length > 1 && sigLogo(g) === sig; });
    }
    if (kind === 'text') {
      return layers().filter(function (l) {
        return l.type === 'text' && !l.locked && sigText(l) === sig;
      }).map(function (l) { return [l]; });
    }
    return groups().filter(function (g) { return g.length === 1 && shapeLike(g[0], ref); });
  }

  function fitInto(layer, b) {
    var a = live();
    if (!a || !layer) return;
    var lw = +layer.widthIn || 0, lh = +layer.heightIn || 0;
    if (!lw || !lh) { lw = b.w; lh = b.h; }
    var s = Math.min((b.w * FIT) / lw, (b.h * FIT) / lh);
    var w = lw * s, h = lh * s;
    a.updateLayer(layer.id, {
      xIn: b.x + (b.w - w) / 2, yIn: b.y + (b.h - h) / 2,
      widthIn: w, heightIn: h
    });
  }

  /* ---- v11: one box, one picture ---------------------------------------- */

  var placed = [];

  function centreOf(b) { return { x: b.x + b.w / 2, y: b.y + b.h / 2 }; }
  function holds(outer, pt) {
    return pt.x >= outer.x && pt.x <= outer.x + outer.w &&
           pt.y >= outer.y && pt.y <= outer.y + outer.h;
  }
  /* a box this run has already filled */
  function claimed(b) {
    var c = centreOf(b);
    for (var i = 0; i < placed.length; i++) if (holds(placed[i], c)) return true;
    return false;
  }
  /* a picture of about this box's own size already sitting over its centre.
     The size test keeps a full board background - far larger than a tile -
     from looking like a filled slot. */
  function imageOver(b) {
    var c = centreOf(b), hit = false;
    layers().forEach(function (l) {
      if (hit || l.type !== 'image') return;
      var q = box(l);
      if (!q.w || !q.h) return;
      if (q.w > b.w * SLOT_MAX || q.h > b.h * SLOT_MAX) return;
      if (holds(q, c)) hit = true;
    });
    return hit;
  }
  /* removeLayer does not take effect in the same turn. Wait for the layers to
     leave the list, then carry on - everything that looks at the board again
     goes through here. */
  function removeAnd(ids, cb) {
    var a = live();
    if (a) ids.forEach(function (id) { try { a.removeLayer(id); } catch (e) {} });
    var t0 = Date.now();
    (function wait() {
      var map = {}, gone = true;
      layers().forEach(function (l) { map[l.id] = 1; });
      ids.forEach(function (id) { if (map[id]) gone = false; });
      if (gone || Date.now() - t0 > FIND_MS) return cb();
      setTimeout(wait, POLL_MS);
    })();
  }

  /* ---- the bar ---------------------------------------------------------- */

  var bar = null, target = null;

  function css() {
    if (document.getElementById('sbdc-logotools-css')) return;
    var st = document.createElement('style');
    st.id = 'sbdc-logotools-css';
    st.textContent =
      '#' + BAR_ID + '{position:fixed !important;z-index:2147483000 !important;display:none;' +
      'gap:6px;align-items:center;background:#fff !important;border:1px solid #cbd5dc !important;' +
      'border-radius:10px !important;padding:5px 7px !important;' +
      'box-shadow:0 6px 20px rgba(12,32,44,.22) !important;' +
      'font:600 13px/1 Archivo,system-ui,sans-serif !important;color:#12232b !important}' +
      '#' + BAR_ID + '.is-on{display:flex !important}' +
      '#' + BAR_ID + ' button{display:inline-flex !important;align-items:center;gap:7px;border:0 !important;' +
      'background:#fff !important;color:#12232b !important;' +
      'font:600 13px/1 Archivo,system-ui,sans-serif !important;padding:7px 10px !important;' +
      'border-radius:8px !important;cursor:pointer;white-space:nowrap;text-transform:none !important;' +
      'letter-spacing:0 !important;box-shadow:none !important;min-height:0 !important}' +
      '#' + BAR_ID + ' button:hover{background:#eef3f6 !important}' +
      '#' + BAR_ID + ' .sbdc-logotools-name{color:#8a97a0 !important;padding:0 8px;max-width:240px;' +
      'overflow:hidden;text-overflow:ellipsis;white-space:nowrap}';
    (document.head || document.documentElement).appendChild(st);
  }

  function make() {
    css();
    bar = document.createElement('div');
    bar.id = BAR_ID;
    bar.setAttribute('role', 'group');
    bar.setAttribute('aria-label', 'Replace with image');

    var b = document.createElement('button');
    b.type = 'button';
    b.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<rect x="3" y="3" width="18" height="18" rx="2"></rect>' +
      '<circle cx="8.5" cy="8.5" r="1.5"></circle>' +
      '<path d="M21 15l-5-5L5 21"></path></svg><span class="sbdc-logotools-label">Replace logo</span>';
    b.addEventListener('click', begin);

    var note = document.createElement('span');
    note.className = 'sbdc-logotools-name';

    bar.appendChild(b);
    bar.appendChild(note);
    document.body.appendChild(bar);
    info.offered = true;
  }

  function place(label, note) {
    if (!bar) return;
    var host = document.querySelector('.canvas-viewport');
    if (!host) return;
    var r2 = host.getBoundingClientRect();
    var lab = bar.querySelector('.sbdc-logotools-label');
    if (lab) lab.textContent = label;
    var n = bar.querySelector('.sbdc-logotools-name');
    if (n) n.textContent = note || '';
    bar.classList.add('is-on');
    var w = bar.offsetWidth || 240;
    bar.style.left = Math.round(r2.left + r2.width / 2 - w / 2) + 'px';
    bar.style.top = Math.round(r2.top + 10) + 'px';
  }

  function hide() { if (bar) bar.classList.remove('is-on'); }

  function noun(kind, g) {
    if (kind === 'logo') return 'this logo';
    if (kind === 'text') {
      var t = String(g[0].text || '').replace(/\s+/g, ' ').trim();
      return t ? '\u201c' + t.slice(0, 22) + '\u201d' : 'this text';
    }
    return 'this shape';
  }

  function show(g, kind) {
    if (window.SBDC914?.legacy.managed()) { target = null; hide(); return; }
    if (!bar) make();
    info.kind = kind;
    var t = { kind: kind, g: g };
    var n = matches(kind, sigOf(t), refOf(t)).length;
    info.matches = n;
    place(kind === 'logo' ? 'Replace logo' : 'Replace with image',
          n > 1 ? (noun(kind, g) + ' and ' + (n - 1) + ' more') : noun(kind, g));
  }

  window.__dcLogoSwapPick = function (g) {
    if (window.SBDC914?.legacy.managed()) return;
    if (!g || !g.length) return;
    var kind = g.length > 1 ? 'logo' : 'shape';
    target = { g: g, kind: kind };
    info.via = 'pad';
    show(g, kind);
  };

  /* ---- the swap --------------------------------------------------------- */

  var pending = null, syncWas = null;

  function quietOthers() {
    if (syncWas === null) syncWas = !!window.__dcTileSyncOff;
    window.__dcTileSyncOff = true;
  }
  function restoreOthers() {
    if (syncWas === null) return;
    window.__dcTileSyncOff = syncWas;
    syncWas = null;
  }

  function begin() {
    if (window.SBDC914?.legacy.managed()) { target = null; hide(); return; }
    var a = live();
    if (!a || !target) return;

    placed = [];
    pending = {
      ids: target.g.map(function (l) { return l.id; }),
      bbox: bboxOf(target.g),
      kind: target.kind,
      sig: sigOf(target),
      ref: refOf(target),
      known: {},
      sawPanel: false,
      away: 0,
      until: Date.now() + WAIT_MS
    };
    layers().forEach(function (l) { if (l.type === 'image') pending.known[l.id] = 1; });
    hide();

    var rail = document.querySelectorAll('.rail-btn');
    for (var i = 0; i < rail.length; i++) {
      if (/^image$/i.test((rail[i].textContent || '').replace(/\s+/g, ' ').trim())) {
        rail[i].click();
        return;
      }
    }
    info.why = 'no Image panel button';
  }

  function onePass(imgId, kind, sig, ref, done) {
    var todo = matches(kind, sig, ref).slice(0, MAX_TILES);
    if (!todo.length) { done(0); return; }
    var did = 0;

    (function next(i) {
      if (i >= todo.length) { done(did); return; }
      var g = todo[i], b = bboxOf(g);
      var a = live();
      if (!a) { done(did); return; }

      /* already filled - clear what is left of the old mark and move on, but
         never put a second picture in the same box */
      if (claimed(b) || imageOver(b)) {
        info.occupied++;
        return removeAnd(g.map(function (l) { return l.id; }), function () { next(i + 1); });
      }

      var before = {};
      layers().forEach(function (l) { if (l.type === 'image') before[l.id] = 1; });

      try { a.duplicateLayer(imgId); }
      catch (e) {
        info.why = 'duplicate failed: ' + (e && e.message);
        info.skipped++;
        return next(i + 1);
      }

      var t0 = Date.now();
      (function wait() {
        var copy = null;
        layers().forEach(function (l) { if (l.type === 'image' && !before[l.id]) copy = l; });
        if (copy) {
          fitInto(copy, b);
          placed.push(b);
          info.siblings++; did++;
          return removeAnd(g.map(function (l) { return l.id; }), function () { next(i + 1); });
        }
        if (Date.now() - t0 > FIND_MS) {
          info.skipped++;
          info.why = 'copy never appeared';
          return next(i + 1);
        }
        setTimeout(wait, POLL_MS);
      })();
    })(0);
  }

  /* sweep again for anything still matching - a slow duplicate should not
     leave a tile behind. Boxes already filled are skipped inside onePass, so a
     re-sweep can no longer stack a second copy. */
  function spread(imgId, kind, sig, ref, done) {
    var round = 0;
    (function go() {
      round++;
      info.rounds = round;
      onePass(imgId, kind, sig, ref, function (did) {
        if (did > 0 && round < ROUNDS && matches(kind, sig, ref).length) return go();
        if (done) done();
      });
    })();
  }

  function finish(img) {
    var p = pending;
    pending = null;
    target = null;

    quietOthers();
    fitInto(img, p.bbox);
    placed.push(p.bbox);
    info.swapped++;

    /* wait for the clicked tile's own pieces to go before looking for the
       rest, or that tile matches again and gets a second picture */
    removeAnd(p.ids, function () {
      spread(img.id, p.kind, p.sig, p.ref, restoreOthers);
    });
    setTimeout(restoreOthers, 30000);
  }

  /* ---- loop ------------------------------------------------------------- */

  setInterval(function () {
    var a = live();
    if (!a) return;
    if (window.SBDC914?.legacy.managed()) { pending = null; target = null; restoreOthers(); hide(); return; }

    if (pending) {
      if (Date.now() > pending.until) { pending = null; restoreOthers(); return; }

      /* the customer left without dropping a file: let go, so a later upload
         made for something else is not taken as this swap's picture */
      if (document.querySelector('.flyout')) { pending.sawPanel = true; pending.away = 0; }
      else if (pending.sawPanel && ++pending.away > PANEL_GRACE) {
        info.why = 'panel closed without a file';
        pending = null; restoreOthers(); return;
      }

      var fresh = null;
      layers().forEach(function (l) {
        if (l.type === 'image' && !pending.known[l.id]) fresh = l;
      });
      if (fresh) {
        try { finish(fresh); }
        catch (e) { info.why = 'error: ' + (e && e.message); pending = null; restoreOthers(); }
      }
      return;
    }

    if (target && info.via === 'pad') info.via = 'selection';

    var sel = a.selectedId;
    if (!sel) { target = null; hide(); return; }
    var layer = null;
    layers().forEach(function (l) { if (l.id === sel) layer = l; });
    if (!layer || layer.locked) { target = null; hide(); return; }

    if (layer.type === 'text') {
      target = { g: [layer], kind: 'text' };
      info.via = 'selection';
      show(target.g, 'text');
      return;
    }
    if (layer.type !== 'shape') { target = null; hide(); return; }

    var g = null;
    groups().forEach(function (cand) {
      if (cand.some(function (l) { return l.id === sel; })) g = cand;
    });
    if (!g) g = [layer];

    target = { g: g, kind: g.length > 1 ? 'logo' : 'shape' };
    info.via = 'selection';
    show(g, target.kind);
  }, TICK);

  window.addEventListener('resize', function () { if (bar && target) show(target.g, target.kind); });
})();
